import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import LanguageLink from "../routing/LanguageLink";
import HeaderComponents from "../ui/HeaderComponents";
import { useAuth } from "../context/AuthContext";
import LanguageSwitcher from "../ui/LanguageSwitcher";
import { useTranslation } from "react-i18next";

const Header = () => {
  const { t, i18n } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { role, logout } = useAuth();

  const isLoggedIn = !!localStorage.getItem("auth_token");

  const navigationItems = [
    { name: t("header.nav.forms"), path: "/forms" },
    { name: t("header.nav.lawyers"), path: "/lawyer" },
    { name: t("header.nav.crm"), path: "/crm" },
    // { name: "Services", path: "/services" },
    { name: t("header.nav.ask_lawyer_ai"), path: "/lawyer-ai" },
    { name: t("header.nav.about"), path: "/about" },
    { name: t("header.nav.blog"), path: "/blog" },
    { name: t("header.nav.contact"), path: "/contact" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const isActive = (path) => location.pathname === path;

  const getProfilePath = () => {
    switch (role) {
      case "office_owner": return "/profile/office";
      case "lawyer": return "/profile/lawyer";
      case "client": return "/profile/client";
      case "paralegal": return "/profile/paralegal";
      default: return "/profile";
    }
  };

  const handleMobileLogout = () => {
    logout();
    setIsMenuOpen(false);
    navigate(`/${i18n.language}/login`);
  };

  return (
    <>
      <nav
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${scrolled ? "bg-white shadow-lg" : "bg-white"
          }`}
        style={{ paddingTop: "env(safe-area-inset-top)" }}
      >
        {/* Top decorative bar */}
        <div className="h-1 bg-blue-600"></div>

        <div className="max-w-[90rem] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo Section */}
            <LanguageLink to="/" className="shrink-0 flex items-center group">
              <div className="transform group-hover:scale-105 transition-transform duration-200">
                <svg
                  width="278"
                  height="64"
                  viewBox="0 0 278 64"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M7.86002 57V46.5H9.04502V57H7.86002ZM11.9253 57V49.245H13.0803V50.55C13.6053 49.77 14.2803 49.08 15.5853 49.08C16.8453 49.08 17.6553 49.74 18.0753 50.625C18.6453 49.77 19.4403 49.08 20.8053 49.08C22.6053 49.08 23.6853 50.265 23.6853 52.2V57H22.5303V52.47C22.5303 50.97 21.7803 50.13 20.5203 50.13C19.3653 50.13 18.3753 51 18.3753 52.53V57H17.2353V52.44C17.2353 50.985 16.4853 50.13 15.2403 50.13C14.0103 50.13 13.0803 51.15 13.0803 52.575V57H11.9253ZM26.1489 57V49.245H27.3039V50.55C27.8289 49.77 28.5039 49.08 29.8089 49.08C31.0689 49.08 31.8789 49.74 32.2989 50.625C32.8689 49.77 33.6639 49.08 35.0289 49.08C36.8289 49.08 37.9089 50.265 37.9089 52.2V57H36.7539V52.47C36.7539 50.97 36.0039 50.13 34.7439 50.13C33.5889 50.13 32.5989 51 32.5989 52.53V57H31.4589V52.44C31.4589 50.985 30.7089 50.13 29.4639 50.13C28.2339 50.13 27.3039 51.15 27.3039 52.575V57H26.1489ZM40.4025 47.55V46.275H41.7225V47.55H40.4025ZM40.4775 57V49.245H41.6325V57H40.4775ZM47.7479 59.43C46.4429 59.43 45.2579 59.07 44.2229 58.35L44.7479 57.45C45.6479 58.095 46.6079 58.44 47.7329 58.44C49.4579 58.44 50.5529 57.495 50.5529 55.68V54.765C49.8779 55.665 48.9329 56.4 47.5079 56.4C45.6479 56.4 43.8629 54.99 43.8629 52.77V52.74C43.8629 50.475 45.6479 49.08 47.5079 49.08C48.9629 49.08 49.8929 49.8 50.5379 50.64V49.245H51.6929V55.65C51.6929 56.85 51.3479 57.75 50.7029 58.395C49.9979 59.1 48.9929 59.43 47.7479 59.43ZM47.7329 55.38C49.2029 55.38 50.5829 54.315 50.5829 52.755V52.725C50.5829 51.135 49.2029 50.1 47.7329 50.1C46.2479 50.1 45.0479 51.12 45.0479 52.71V52.74C45.0479 54.3 46.2929 55.38 47.7329 55.38ZM54.2739 57V49.245H55.4289V51.27C55.9989 49.98 57.1239 49.065 58.5939 49.11V50.355H58.5039C56.8089 50.355 55.4289 51.57 55.4289 53.91V57H54.2739ZM62.2821 57.165C60.8421 57.165 59.3871 56.355 59.3871 54.75V54.72C59.3871 53.1 60.7371 52.215 62.7021 52.215C63.7071 52.215 64.3821 52.35 65.0871 52.545V52.275C65.0871 50.88 64.2321 50.16 62.7771 50.16C61.8621 50.16 61.1421 50.385 60.4221 50.73L60.0771 49.785C60.9321 49.38 61.7571 49.14 62.8971 49.14C63.9921 49.14 64.8321 49.425 65.4021 49.995C65.9271 50.52 66.1971 51.285 66.1971 52.26V57H65.0871V55.83C64.5471 56.535 63.6621 57.165 62.2821 57.165ZM62.5071 56.25C63.9471 56.25 65.1021 55.38 65.1021 54.15V53.4C64.5171 53.235 63.7821 53.07 62.8221 53.07C61.3671 53.07 60.5571 53.7 60.5571 54.675V54.705C60.5571 55.68 61.4721 56.25 62.5071 56.25ZM71.1804 57.135C69.9204 57.135 68.9904 56.52 68.9904 54.945V50.265H67.9104V49.245H68.9904V46.905H70.1454V49.245H72.6054V50.265H70.1454V54.795C70.1454 55.755 70.6704 56.085 71.4504 56.085C71.8554 56.085 72.1704 56.01 72.5754 55.815V56.805C72.1704 57.03 71.7354 57.135 71.1804 57.135ZM74.6799 47.55V46.275H75.9999V47.55H74.6799ZM74.7549 57V49.245H75.9099V57H74.7549ZM82.1303 57.18C79.8053 57.18 78.1103 55.35 78.1103 53.16V53.13C78.1103 50.925 79.8053 49.08 82.1603 49.08C84.5003 49.08 86.1953 50.91 86.1953 53.1V53.13C86.1953 55.335 84.4703 57.18 82.1303 57.18ZM82.1603 56.145C83.8403 56.145 85.0103 54.78 85.0103 53.16V53.13C85.0103 51.465 83.7653 50.1 82.1303 50.1C80.4503 50.1 79.2953 51.465 79.2953 53.1V53.13C79.2953 54.795 80.5253 56.145 82.1603 56.145ZM88.2876 57V49.245H89.4426V50.595C89.9676 49.755 90.7626 49.08 92.0976 49.08C93.9576 49.08 95.0676 50.34 95.0676 52.185V57H93.9126V52.47C93.9126 51.03 93.1476 50.13 91.7676 50.13C90.4326 50.13 89.4426 51.105 89.4426 52.56V57H88.2876ZM105.823 57.15C104.143 57.15 102.793 56.61 101.533 55.47L102.268 54.6C103.363 55.605 104.413 56.085 105.868 56.085C107.293 56.085 108.208 55.335 108.208 54.3V54.27C108.208 53.295 107.683 52.755 105.478 52.275C103.063 51.75 101.953 50.97 101.953 49.245V49.215C101.953 47.58 103.408 46.35 105.403 46.35C106.948 46.35 108.013 46.785 109.093 47.64L108.403 48.555C107.428 47.76 106.453 47.415 105.373 47.415C103.993 47.415 103.138 48.15 103.138 49.11V49.14C103.138 50.13 103.663 50.7 105.988 51.18C108.328 51.705 109.408 52.545 109.408 54.15V54.18C109.408 55.98 107.908 57.15 105.823 57.15ZM115.013 57.18C112.898 57.18 111.143 55.545 111.143 53.145V53.115C111.143 50.88 112.703 49.08 114.863 49.08C117.158 49.08 118.478 50.91 118.478 53.175C118.478 53.34 118.478 53.415 118.463 53.55H112.313C112.478 55.23 113.663 56.175 115.043 56.175C116.123 56.175 116.843 55.74 117.488 55.08L118.208 55.725C117.428 56.61 116.498 57.18 115.013 57.18ZM112.313 52.695H117.308C117.188 51.3 116.393 50.055 114.833 50.055C113.468 50.055 112.478 51.195 112.313 52.695ZM120.499 57V49.245H121.654V51.27C122.224 49.98 123.349 49.065 124.819 49.11V50.355H124.729C123.034 50.355 121.654 51.57 121.654 53.91V57H120.499ZM129.174 57.06L125.814 49.245H127.089L129.699 55.68L132.324 49.245H133.569L130.194 57.06H129.174ZM135.412 47.55V46.275H136.732V47.55H135.412ZM135.487 57V49.245H136.642V57H135.487ZM142.818 57.18C140.538 57.18 138.843 55.335 138.843 53.16V53.13C138.843 50.925 140.538 49.08 142.818 49.08C144.288 49.08 145.188 49.68 145.923 50.46L145.158 51.27C144.543 50.625 143.868 50.1 142.803 50.1C141.228 50.1 140.028 51.42 140.028 53.1V53.13C140.028 54.81 141.258 56.145 142.878 56.145C143.883 56.145 144.633 55.65 145.248 54.99L145.983 55.68C145.203 56.565 144.273 57.18 142.818 57.18ZM151.062 57.18C148.947 57.18 147.192 55.545 147.192 53.145V53.115C147.192 50.88 148.752 49.08 150.912 49.08C153.207 49.08 154.527 50.91 154.527 53.175C154.527 53.34 154.527 53.415 154.512 53.55H148.362C148.527 55.23 149.712 56.175 151.092 56.175C152.172 56.175 152.892 55.74 153.537 55.08L154.257 55.725C153.477 56.61 152.547 57.18 151.062 57.18ZM148.362 52.695H153.357C153.237 51.3 152.442 50.055 150.882 50.055C149.517 50.055 148.527 51.195 148.362 52.695ZM159.114 57.15C157.989 57.15 156.789 56.715 155.904 56.01L156.489 55.185C157.329 55.83 158.259 56.175 159.174 56.175C160.119 56.175 160.779 55.71 160.779 54.945V54.915C160.779 54.15 159.864 53.835 158.844 53.55C157.644 53.19 156.279 52.8 156.279 51.36V51.33C156.279 49.995 157.389 49.11 158.919 49.11C159.879 49.11 160.914 49.44 161.709 49.965L161.184 50.835C160.464 50.37 159.654 50.085 158.889 50.085C157.974 50.085 157.389 50.565 157.389 51.21V51.24C157.389 51.975 158.349 52.26 159.384 52.575C160.584 52.935 161.874 53.4 161.874 54.795V54.825C161.874 56.295 160.659 57.15 159.114 57.15Z"
                    fill="black"
                  />
                  <path
                    d="M17.52 39.2C12.24 39.2 8.2 37.9867 5.4 35.56C2.6 33.1067 1.2 29.48 1.2 24.68C1.2 19.9067 2.6 16.2933 5.4 13.84C8.2 11.3867 12.24 10.1733 17.52 10.2L31.92 10.28V15.4H17.96C14.4933 15.4 11.92 16.1333 10.24 17.6C8.56 19.0667 7.72 21.4267 7.72 24.68C7.72 27.9333 8.56 30.3067 10.24 31.8C11.92 33.2667 14.4933 34 17.96 34H32.32V39.2H17.52ZM35.7156 39V18.4H41.5156V39H35.7156ZM35.6756 15V10.48H41.5556V15H35.6756ZM56.6309 39C54.3109 39 52.6043 38.4267 51.5109 37.28C50.4176 36.1067 49.8709 34.5733 49.8709 32.68V23H43.9109V18.4H49.8709V12.08H55.6709V18.4H64.2309V23H55.6709V31.96C55.6709 33.5867 56.4709 34.4 58.0709 34.4H65.3509V39H56.6309ZM68.3328 39V18.4H74.1328V39H68.3328ZM68.2928 15V10.48H74.1728V15H68.2928ZM77.9281 39V34.76L94.1681 23H78.6081V18.4H102.648V22.64L86.4081 34.4H102.648V39H77.9281ZM117.018 39.2C113.444 39.2 110.658 38.32 108.658 36.56C106.658 34.7733 105.658 32.16 105.658 28.72C105.658 25.2533 106.658 22.64 108.658 20.88C110.658 19.0933 113.444 18.2 117.018 18.2H127.018C129.364 18.2 131.151 18.76 132.378 19.88C133.631 20.9733 134.258 22.5467 134.258 24.6C134.258 28.7867 131.844 30.88 127.018 30.88H111.898C112.458 33.36 114.164 34.6 117.018 34.6H133.218V39.2H117.018ZM117.018 22.8C114.271 22.8 112.578 23.96 111.938 26.28H126.738C127.271 26.28 127.684 26.12 127.978 25.8C128.298 25.48 128.458 25.04 128.458 24.48C128.458 23.36 127.884 22.8 126.738 22.8H117.018ZM137.864 39V18.4H155.824C158.171 18.4 159.877 18.9867 160.944 20.16C162.037 21.3067 162.584 22.8267 162.584 24.72V39H156.784V25.4C156.784 23.8 155.984 23 154.384 23H143.664V39H137.864Z"
                    fill="blue"
                  />
                  <path
                    d="M171.784 21.44C171.784 19.264 171.485 17.664 170.888 16.64C170.291 15.5733 169.096 15.04 167.304 15.04H166.024V13.76H171.784C173.363 13.76 174.536 13.9733 175.304 14.4C176.115 14.784 176.691 15.5093 177.032 16.576C177.373 17.6427 177.544 19.264 177.544 21.44V32.96H196.744V13.76H202.504V48.32C202.504 50.496 202.803 52.1173 203.4 53.184C203.997 54.208 205.192 54.72 206.984 54.72H208.264V56H202.504C200.925 56 199.731 55.808 198.92 55.424C198.152 54.9973 197.597 54.2507 197.256 53.184C196.915 52.1173 196.744 50.496 196.744 48.32V35.52H177.544V56H171.784V21.44ZM224.927 56.64C221.727 56.64 219.103 55.7867 217.055 54.08C215.05 52.3733 214.047 49.3867 214.047 45.12V33.6C214.047 31.424 213.748 29.824 213.151 28.8C212.554 27.7333 211.359 27.2 209.567 27.2H208.927V25.92H214.047C215.626 25.92 216.799 26.1333 217.567 26.56C218.378 26.944 218.954 27.6693 219.295 28.736C219.636 29.8027 219.807 31.424 219.807 33.6V45.12C219.807 48.1067 220.468 50.304 221.791 51.712C223.156 53.0773 224.842 53.76 226.847 53.76C229.108 53.76 231.242 52.6293 233.247 50.368V25.92H239.007V56H235.807L233.567 53.44H233.247C232.351 54.336 231.178 55.104 229.727 55.744C228.319 56.3413 226.719 56.64 224.927 56.64ZM262.633 56.64C260.457 56.64 258.665 56.2987 257.257 55.616C255.849 54.9333 254.761 54.1013 253.993 53.12H253.673L251.433 56H248.233V21.44C248.233 19.264 247.934 17.664 247.337 16.64C246.74 15.5733 245.545 15.04 243.753 15.04H242.473V13.76H248.233C249.812 13.76 250.985 13.9733 251.753 14.4C252.564 14.784 253.14 15.5093 253.481 16.576C253.822 17.6427 253.993 19.264 253.993 21.44V27.2C255.23 26.6027 256.468 26.1333 257.705 25.792C258.942 25.4507 260.372 25.28 261.993 25.28C265.961 25.28 268.969 26.752 271.017 29.696C273.108 32.64 274.153 36.3947 274.153 40.96C274.153 45.4827 273.15 49.2373 271.145 52.224C269.14 55.168 266.302 56.64 262.633 56.64ZM260.713 54.08C263.145 54.08 264.98 52.8853 266.217 50.496C267.454 48.064 268.073 44.8853 268.073 40.96C268.073 36.992 267.412 33.8133 266.089 31.424C264.809 29.0347 262.804 27.84 260.073 27.84C259.177 27.84 258.26 28.0107 257.321 28.352C256.382 28.6933 255.273 29.1627 253.993 29.76V50.24C254.761 51.392 255.657 52.3307 256.681 53.056C257.748 53.76 259.092 54.08 260.713 54.08Z"
                    fill="black"
                  />
                </svg>
              </div>
            </LanguageLink>

            {/* Desktop Navigation - Centered */}
            <div className="hidden lg:flex flex-1 justify-center">
              <div className="flex items-center space-x-1">
                {navigationItems.map((item) => (
                  <div key={item.name} className="relative">
                    {item.path.startsWith("/") ? (
                      <LanguageLink
                        to={item.path}
                        className={`relative px-6 py-3 text-sm font-semibold transition-all duration-300 ${isActive(item.path)
                          ? "text-blue-600"
                          : "text-gray-700 hover:text-blue-600"
                          }`}
                      >
                        {item.name}
                        {isActive(item.path) && (
                          <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600"></span>
                        )}
                        <span className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-0 h-0.5 bg-blue-600 transition-all duration-300 group-hover:w-full"></span>
                      </LanguageLink>
                    ) : (
                      <a
                        href={item.path}
                        className="relative px-6 py-3 text-sm font-semibold text-gray-700 hover:text-blue-600 transition-all duration-300"
                      >
                        {item.name}
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Right side - Language Switcher & Action buttons */}
            <div className="hidden lg:flex items-center space-x-3">
              <LanguageSwitcher />
              <HeaderComponents />
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setIsMenuOpen((s) => !s)}
              className="lg:hidden p-2 rounded-lg text-gray-700 hover:text-blue-600 hover:bg-blue-50 transition-all duration-200"
              aria-label="Toggle menu"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                {isMenuOpen ? (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                ) : (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile menu - Slide down animation */}
        <div
          className={`lg:hidden overflow-hidden transition-all duration-300 ease-in-out ${isMenuOpen ? "max-h-full opacity-100" : "max-h-0 opacity-0"
            }`}
        >
          <div className="bg-white border-t-2 border-blue-600 px-4 py-6 space-y-1">
            {navigationItems.map((item) =>
              item.path.startsWith("/") ? (
                <LanguageLink
                  key={item.name}
                  to={item.path}
                  onClick={() => setIsMenuOpen(false)}
                  className={`block px-4 py-3 rounded-lg text-base font-semibold transition-all duration-200 ${isActive(item.path)
                    ? "bg-blue-50 text-blue-600 border-l-4 border-blue-600"
                    : "text-gray-700 hover:bg-blue-50 hover:text-blue-600"
                    }`}
                >
                  {item.name}
                </LanguageLink>
              ) : (
                <a
                  key={item.name}
                  href={item.path}
                  onClick={() => setIsMenuOpen(false)}
                  className="block px-4 py-3 rounded-lg text-base font-semibold text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-all duration-200"
                >
                  {item.name}
                </a>
              )
            )}

            {/* Mobile Action Buttons - نفس لوجيك HeaderComponents */}
            <div className="pt-4 mt-4 border-t border-gray-200 space-y-2">
              {/* Language Switcher for Mobile */}
              <div className="mb-3">
                <LanguageSwitcher />
              </div>

              {isLoggedIn ? (
                <>
                  <LanguageLink
                    to={getProfilePath()}
                    onClick={() => setIsMenuOpen(false)}
                    className="flex items-center justify-center px-4 py-3 rounded-lg bg-gray-100 text-gray-700 hover:bg-blue-50 hover:text-blue-600 font-semibold transition-all duration-200"
                  >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    {t("header.nav.profile")}
                  </LanguageLink>

                  <button
                    onClick={handleMobileLogout}
                    className="flex items-center justify-center w-full px-4 py-3 rounded-lg bg-red-100 text-red-600 hover:bg-red-200 font-semibold transition-all duration-200"
                  >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    {t("header.nav.logout")}
                  </button>
                </>
              ) : (
                <>
                  <LanguageLink
                    to="/login"
                    onClick={() => setIsMenuOpen(false)}
                    className="block w-full text-center px-6 py-3 rounded-lg border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white font-semibold transition-all duration-200"
                  >
                    {t("header.nav.login")}
                  </LanguageLink>
                  <LanguageLink
                    to="/register"
                    onClick={() => setIsMenuOpen(false)}
                    className="block w-full text-center px-6 py-3 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-semibold shadow-md transition-all duration-200"
                  >
                    {t("header.nav.get_started")}
                  </LanguageLink>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      <div className="h-20" />
    </>
  );
};

export default Header;